import React from 'react';
import clsx from 'clsx';
import { EllipsisVertical, GripVertical } from 'lucide-react';
import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import Tooltip from '@dashboard/components/Tooltip';
import { Badge } from '@dashboard/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@dashboard/components/ui/accordion';
import { useVariantHelpers } from '../useVariantHelpers';
import VariantEdit from './VariantEdit';
import { useVariantEdit } from '../VariantEditContext';

export default function VariantItem({ variant, dragHandleProps }) {
  const { isAddingVariant, setIsEditingVariant } = useVariantEdit();

  const { onEnableDisableVariant, onDuplicateVariant, onDeleteVariant, onCancelAddVariant } =
    useVariantHelpers(variant._id || variant.key);

  const enabledOptionsCount = variant.options.filter(option => option.status === 'enabled').length;

  const variantId = variant._id || variant.key;

  return (
    <AccordionItem value={variantId} className="border-b-0">
      <div className="sticky top-0 bg-white dark:bg-background z-10 flex flex-row items-center gap-2 px-1 rounded-t">
        <div
          {...dragHandleProps}
          className={clsx(
            buttonVariants({ variant: 'ghost', size: 'icon' }),
            'cursor-grab active:cursor-grabbing',
          )}
        >
          <GripVertical className="size-4" />
        </div>

        <div className="flex-1 min-w-0">
          <AccordionTrigger
            className={clsx('py-3 items-center min-w-0 hover:no-underline', {
              'cursor-not-allowed': isAddingVariant,
            })}
            disabled={variant.isNew}
          >
            {!variant.isNew && (
              <span
                className={clsx('font-medium truncate', {
                  'opacity-50': variant.status === 'disabled',
                })}
              >
                {variant.name}
                <span>{variant.required ? ' *' : ''}</span>
                {variant.hidden && (
                  <Badge variant="secondary" className="ml-2">
                    Hidden
                  </Badge>
                )}
                {variant.showHideLogic?.enabled && (
                  <Badge variant="secondary" className="ml-2">
                    Conditional
                  </Badge>
                )}
              </span>
            )}

            <div className="flex gap-x-2 items-center ml-auto shrink-0">
              {!variant.isNew && (
                <>
                  {variant.status === 'disabled' && (
                    <Tooltip label="Disabled variants will not be shown to customers">
                      <Badge variant="secondary" className="ml-2">
                        Disabled
                      </Badge>
                    </Tooltip>
                  )}

                  <span className="text-xs text-gray-500 dark:text-muted-foreground whitespace-nowrap">
                    {enabledOptionsCount} option
                    {enabledOptionsCount === 1 ? '' : 's'}
                  </span>
                </>
              )}
            </div>
          </AccordionTrigger>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={isAddingVariant}>
            <ShadcnButton
              variant="ghost"
              size="icon"
              aria-label="More options"
              disabled={isAddingVariant}
            >
              <EllipsisVertical className="size-4" />
            </ShadcnButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onDuplicateVariant}>Duplicate</DropdownMenuItem>
            <DropdownMenuItem onSelect={onEnableDisableVariant}>
              {variant.status === 'enabled' ? 'Disable' : 'Enable'}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onDeleteVariant} variant="destructive">
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AccordionContent
        className={clsx('pb-4 pt-2 relative', {
          'opacity-50 pointer-events-none': variant.status === 'disabled',
        })}
      >
        <VariantEdit
          variant={variant}
          cancel={() => {
            if (variant.isNew) {
              onCancelAddVariant();
            } else {
              setIsEditingVariant(false, variant._id);
            }
          }}
        />
      </AccordionContent>
    </AccordionItem>
  );
}
